import { useEffect, useRef, useState } from "react";

import { decodeJwtClaims } from "../auth/jwt";
import {
  checkPatientEligibility,
  claimsIndicateUserEligibility,
  type PpEligibilityReason,
} from "../auth/ppEligibility";
import { ensurePpAccessTokenDetailed, hasPersistedPpLink, invalidatePpLink } from "../auth/ppToken";

export interface PpCreateAuthPreflightArgs {
  /** Identity currently signed in (Entra/Pathways/PP) — see ppToken.ts's resolveIdentityEmail. */
  identityEmail: string | undefined;
  /** Provider Portal API base URL WITHOUT a trailing `/api` (raw
   *  `VITE_PROVIDER_PORTAL_API_BASE_URL` — see ppEligibility.ts's
   *  `PatientEligibilityArgs` doc for why this differs from the
   *  `/api`-suffixed `ppApiBaseUrl` prop fed to the Authorizations module). */
  ppApiBaseUrl: string | undefined;
  companyId: string | undefined;
  /** Best-available PP member id hint (RmiMembId / handoffMemberIdHint). */
  memberId: string | undefined;
  userId: string | undefined;
  userRole: string | undefined;
}

export interface PpCreateAuthPreflight {
  /**
   * False ONLY once a LINKED account has been positively found ineligible.
   * `true` covers both "eligible" and "unlinked / offer-link" (design doc
   * §5a: an unlinked account is never hard-hidden) and the transient
   * "still checking" state (no flicker-disable while a check is in flight).
   */
  gateOpen: boolean;
  reason?: PpEligibilityReason;
  /** True once a persisted PP refresh token bound to this identity exists. */
  hasLink: boolean;
}

const NO_PATIENT_KEY = "";

function patientKeyOf(companyId: string | undefined, memberId: string | undefined): string {
  if (!companyId || !memberId) return NO_PATIENT_KEY;
  return `${companyId}:${memberId}`;
}

/**
 * How long a LINKED identity is allowed to sit with missing patient-context
 * args (companyId/memberId/ppApiBaseUrl) before the gate stops fail-opening
 * and resolves to ineligible instead (see CONCERN 1 fix below). Generous
 * enough to cover a normal host-side member-lookup query round trip.
 */
const PATIENT_CONTEXT_TIMEOUT_MS = 8_000;

/**
 * How long a LINKED identity's silent PP-token mint is allowed to sit
 * unsettled before this hook stops trusting it for gating — the same
 * "legitimately mid-flight vs. permanently stuck, no loading signal to tell
 * them apart" problem as `PATIENT_CONTEXT_TIMEOUT_MS` above, this time for
 * `ensurePpAccessTokenDetailed`'s silent mint effect below.
 *
 * A normal refresh FAILURE already self-recovers with no timeout needed:
 * `ensurePpAccessTokenDetailed`'s catch clears the dead stash, so `hasLink`
 * (recomputed fresh from `hasPersistedPpLink` every render) flips false on
 * the very next render and falls to the "unlinked" branch below. This bound
 * exists only for a genuinely HUNG request — `ropcRefresh`'s `fetch()`
 * (auth/b2cClient.ts) has no AbortController/timeout, so a hung request
 * never settles at all, never clears the stash, and without this bound
 * `hasLink` would read true FOREVER while `resolvedAccessToken`
 * (MemberProfilePage.tsx) withholds the module's `accessToken` prop with no
 * recovery. Deliberately does NOT touch `ropcRefresh`/`b2cClient.ts`'s shared
 * in-flight-refresh mutex — the underlying fetch is left running untouched
 * (no caller can abort it without new AbortController plumbing there), so
 * other callers sharing that promise are unaffected; this only bounds how
 * long THIS hook's exposed `hasLink` is willing to wait on it before falling
 * back to the sentinel → PpLoginModal recovery path.
 */
const SILENT_MINT_TIMEOUT_MS = 8_000;

/**
 * ACT-1247 SSO-PP auth-linking Phase 1 (design doc §5a) — silent eligibility
 * pre-flight for Create Auth.
 *
 * When a persisted PP link exists for the current identity (see
 * auth/ppToken.ts's `hasPersistedPpLink`), this silently mints a fresh PP
 * access token (`ensurePpAccessToken` — no modal) and resolves BOTH
 * user- and patient-eligibility before Create Auth is surfaced, so an
 * ineligible click fails up-front and explained instead of late (an APIM 401
 * or an empty `GetAuthMember` at mint time).
 *
 * Caching (per the design doc): user-eligibility is cached for the session,
 * keyed by identity — a new sign-in (a different `identityEmail`) resets it.
 * Patient-eligibility re-runs on patient change (keyed by companyId+memberId).
 *
 * `claimsIndicateUserEligibility` is a fast, claims-only HINT — it is NEVER
 * the sole basis for hard-blocking Create Auth (its claim shape is
 * unconfirmed, see ppEligibility.ts). Only `checkPatientEligibility`'s real
 * `GetAuthMember` round trip is ground truth. A 200 response (eligible OR
 * member-not-found — either one proves the token cleared APIM) upgrades a
 * false-negative guess to `confirmed: true` eligible, which is then
 * authoritative for every subsequent patient too (APIM user-eligibility
 * doesn't vary per patient) — the claims hint is never consulted again for
 * that identity.
 *
 * AUTH-ERROR HANDLING (AM-5706): an HTTP 401/403 (`reason:
 * "no-pp-permission"`) means APIM rejected the TOKEN. Whether that's genuine
 * ineligibility or the known AM-5692 group-claim-dropping B2C bug depends on
 * how the rejected token was minted, so we branch on its ORIGIN
 * (`fromInteractiveLogin`, threaded through `ensurePpAccessTokenDetailed`):
 *
 *   - REFRESH-derived token (`ropcRefresh`, the silent-mint path): AMBIGUOUS —
 *     a refreshed token can be missing its `QA.ProviderPortal.<IPA>` group
 *     claim (AM-5692). We NEVER cache this as a durable "user not eligible"
 *     verdict; instead we treat it as "PP token stale / re-link needed": drop
 *     the dead stash (`invalidatePpLink`) and clear the in-memory token so the
 *     gate falls to the unlinked / offer-link path (PpLoginModal re-mints a
 *     FRESH, non-refresh token that carries the group claim). Decouples the
 *     feature from the AM-5692 policy-deploy timing.
 *   - INTERACTIVE-login token (`ropcLogin` / PpLoginModal): the group claim
 *     is guaranteed present, so a 401 can only mean GENUINE ineligibility. We
 *     set the `confirmed: true` INELIGIBLE verdict (previously unreachable)
 *     and surface the clear "no Authorization permission / IPA group" message
 *     — we do NOT re-link. This is what closes the infinite re-link loop: a
 *     genuinely-ineligible user who re-links gets a fresh token that still
 *     fails, and without this branch would be bounced back to the login modal
 *     forever with no "you're not eligible" explanation.
 *
 * Net: the refresh-401 re-link path stays the AM-5692 fix; the very NEXT
 * (interactive) 401 terminates in a confirmed denial — one re-link at most,
 * never a loop. The stash is kept on a confirmed denial (it's a valid link,
 * just not an eligible account).
 *
 * Fails closed (`gateOpen: false, reason: "preflight-error"`) on any
 * non-auth check error, and resolves to `"not-pp-eligible"` (not an
 * indefinite fail-open) if the patient-context args needed to even run the
 * check stay missing past `PATIENT_CONTEXT_TIMEOUT_MS`.
 */
export function usePpCreateAuthPreflight(
  args: PpCreateAuthPreflightArgs,
): PpCreateAuthPreflight {
  const hasLink = hasPersistedPpLink(args.identityEmail);

  // Silent mint on profile load / identity change — never opens
  // PpLoginModal. A failed/absent refresh just leaves accessToken
  // undefined, which keeps the gate open (still-checking / no-op) rather
  // than disabling — ensurePpSession's click-time retry is the real
  // fallback path to PpLoginModal, not this pre-flight.
  //
  // The token carries its ORIGIN (`fromInteractiveLogin`) so the auth-error
  // handling below can tell an ambiguous refresh-derived 401 (AM-5692 →
  // re-link) apart from a fresh-login 401 (genuine ineligibility → confirmed
  // denial, no re-link loop; AM-5706).
  const [ppAccess, setPpAccess] = useState<
    { token: string; fromInteractiveLogin: boolean } | undefined
  >(undefined);
  const accessToken = ppAccess?.token;
  const tokenFromInteractiveLogin = ppAccess?.fromInteractiveLogin ?? false;
  const mintedForIdentityRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!hasLink || !args.identityEmail) {
      mintedForIdentityRef.current = undefined;
      setPpAccess(undefined);
      return;
    }
    if (mintedForIdentityRef.current === args.identityEmail) return;
    mintedForIdentityRef.current = args.identityEmail;
    let cancelled = false;
    void ensurePpAccessTokenDetailed(args.identityEmail).then((minted) => {
      if (!cancelled) setPpAccess(minted);
    });
    return () => {
      cancelled = true;
    };
  }, [hasLink, args.identityEmail]);

  // Bounded fail-open for a HUNG silent mint — see SILENT_MINT_TIMEOUT_MS's
  // doc comment above. Armed only while linked, identified, and still
  // waiting on a token (`!accessToken`); a real settle (success — accessToken
  // becomes truthy — or failure — hasLink flips false via the cleared stash)
  // disarms it on the very next render, same fail-open/reset shape as
  // `contextTimedOut` below.
  const [silentMintHung, setSilentMintHung] = useState(false);
  useEffect(() => {
    if (!hasLink || !args.identityEmail || accessToken) {
      setSilentMintHung(false);
      return;
    }
    const timer = setTimeout(() => setSilentMintHung(true), SILENT_MINT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [hasLink, args.identityEmail, accessToken]);

  // Session-cached user-eligibility, keyed by identity. `confirmed: false`
  // means "claims-only guess" (a fast HINT, never a hard block on its own);
  // `confirmed: true` means a real `checkPatientEligibility` round trip
  // settled it and it's authoritative for every patient under this identity,
  // not just the one that produced it.
  //
  // Fix 3 (MINOR): this is React STATE, not a ref, so every render reflects
  // the latest verdict directly rather than relying on a co-incident
  // `setPatientResult` to trigger the re-render that would surface a ref
  // mutation. Behaviour is otherwise identical.
  const [userEligible, setUserEligible] = useState<{
    identityEmail: string;
    eligible: boolean;
    confirmed: boolean;
  } | null>(null);
  // Per-patient cache (real GetAuthMember round trip).
  const [patientResult, setPatientResult] = useState<
    { key: string; eligible: boolean; reason?: PpEligibilityReason } | null
  >(null);

  const patientKey = patientKeyOf(args.companyId, args.memberId);
  const missingPatientContext = !args.companyId || !args.memberId || !args.ppApiBaseUrl;

  // CONCERN 1 fix: companyId/memberId/ppApiBaseUrl can be legitimately
  // mid-flight (e.g. the host's own member-lookup query hasn't resolved
  // yet) OR permanently absent (e.g. a Pathways member with `ipaCode ===
  // "NA"`, or the PP API base env var simply unset) — this hook has no
  // "loading" signal from the host to tell those apart. Fail-open only for
  // a bounded grace window; past it, treat the context as permanently
  // unavailable rather than reading `gateOpen: true` forever.
  const [contextTimedOut, setContextTimedOut] = useState(false);
  useEffect(() => {
    if (!hasLink || !missingPatientContext) {
      setContextTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setContextTimedOut(true), PATIENT_CONTEXT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [hasLink, missingPatientContext]);

  useEffect(() => {
    if (!accessToken || missingPatientContext) return;

    // Seed a claims-only guess for a NEW identity (idempotent — a functional
    // update returns the previous object unchanged when the identity already
    // has a verdict, so this neither re-renders nor loops the effect). A
    // fresh guess always starts unconfirmed — even if a previous identity had
    // a confirmed result, a new identity has to re-earn it.
    setUserEligible((prev) => {
      if (prev && prev.identityEmail === (args.identityEmail ?? "")) return prev;
      const { eligible } = claimsIndicateUserEligibility(
        decodeJwtClaims(accessToken) as Record<string, unknown> | null,
      );
      return { identityEmail: args.identityEmail ?? "", eligible, confirmed: false };
    });

    if (patientResult?.key === patientKey) return; // already resolved for this patient

    let cancelled = false;
    void checkPatientEligibility({
      ppApiBaseUrl: args.ppApiBaseUrl as string,
      accessToken,
      companyId: args.companyId as string,
      memberId: args.memberId as string,
      userId: args.userId,
      userRole: args.userRole,
    }).then((result) => {
      if (cancelled) return;
      if (result.reason === "no-pp-permission") {
        // AUTH error (HTTP 401/403) — APIM rejected the TOKEN. What that means
        // depends on how the rejected token was minted (AM-5706):
        if (tokenFromInteractiveLogin) {
          // The token came from a FRESH interactive login (PpLoginModal /
          // ropcLogin), which always carries the `QA.ProviderPortal.<IPA>`
          // group claim — so the AM-5692 "refresh dropped the claim" cause is
          // ruled out. A 401 here can only be GENUINE ineligibility (the linked
          // PP account really lacks the Authorization permission / IPA group).
          // Confirm the denial (the previously-unreachable `confirmed: true`
          // INELIGIBLE verdict) and surface the clear "not eligible" message —
          // do NOT invalidate + re-link, which would bounce this user into the
          // login modal again and again on a fresh token that can never
          // succeed (the infinite re-link loop this fix closes). We KEEP the
          // stash (it's a valid link, just not an eligible one) and set
          // patientResult too so this effect is idempotent (the `patientResult
          // ?.key === patientKey` guard above short-circuits any re-run — no
          // re-check loop).
          setUserEligible({
            identityEmail: args.identityEmail ?? "",
            eligible: false,
            confirmed: true,
          });
          setPatientResult({ key: patientKey, eligible: false, reason: "no-pp-permission" });
          return;
        }
        // The token was REFRESH-derived — AMBIGUOUS: genuine ineligibility OR a
        // refresh-derived token missing its group claim (AM-5692). NEVER cache
        // this as a durable, all-patients ineligible verdict. Drop the dead
        // stash and clear the in-memory token so the gate falls to the
        // unlinked / offer-link (re-link) path — a fresh PpLoginModal login
        // mints a non-refresh token that carries the claim, decoupling the
        // feature from the AM-5692 policy-deploy timing. Clearing accessToken
        // also re-arms this effect's `!accessToken` guard, so there is no
        // re-check loop (and in a real browser `hasPersistedPpLink` now reads
        // the cleared stash as false → `gateOpen: true, reason: "unlinked"`).
        // If that fresh re-login is ALSO denied, its token IS interactive, so
        // it lands in the `confirmed`-denial branch above instead of looping.
        invalidatePpLink(args.identityEmail);
        setPpAccess(undefined);
        return;
      }
      if (result.reason !== "preflight-error") {
        // A 200 response — whether `eligible` (>=1 row) or "member-not-found"
        // (0 rows) — proves the token cleared APIM before EZCAP was ever
        // reached. That's real ground truth for USER eligibility too
        // (independent of whether THIS patient has an EZCAP record), so it
        // upgrades a false-negative claims guess to eligible, confirmed. A
        // bare "preflight-error" (network/parse failure) is ambiguous and
        // intentionally left untouched — it neither confirms nor denies.
        setUserEligible({ identityEmail: args.identityEmail ?? "", eligible: true, confirmed: true });
      }
      setPatientResult({ key: patientKey, eligible: result.eligible, reason: result.reason });
    });
    return () => {
      cancelled = true;
    };
  }, [
    accessToken,
    tokenFromInteractiveLogin,
    args.companyId,
    args.memberId,
    args.ppApiBaseUrl,
    args.identityEmail,
    args.userId,
    args.userRole,
    missingPatientContext,
    patientKey,
    patientResult?.key,
  ]);

  if (!hasLink) return { gateOpen: true, hasLink: false, reason: "unlinked" };

  if (missingPatientContext) {
    // Still within the grace window — could just be a mid-flight host query;
    // don't hard-block yet (matches the "no flicker-disable while a check is
    // in flight" posture). Past it, this identity/patient combination can
    // never be checked — resolve closed instead of open-forever.
    return contextTimedOut
      ? { gateOpen: false, hasLink: true, reason: "not-pp-eligible" }
      : { gateOpen: true, hasLink: true };
  }

  // A CONFIRMED (real round-trip) ineligibility is ground truth for this
  // identity across every patient — no need to wait on THIS patient's own
  // round trip to know Create Auth must stay blocked. Reached (AM-5706) when a
  // FRESH interactive-login token — one that provably carries the group claim
  // — is still rejected 401/403 by APIM: genuine ineligibility, surfaced as a
  // clear denial instead of another re-link (see the auth-error branch in the
  // effect above). Fail-CLOSED: this returns gate CLOSED.
  if (
    userEligible?.identityEmail === (args.identityEmail ?? "") &&
    userEligible.confirmed &&
    !userEligible.eligible
  ) {
    return { gateOpen: false, hasLink: true, reason: "no-pp-permission" };
  }

  // Still resolving (minting). `hasLink` here is `!silentMintHung`, not a bare
  // `true`: this is exactly the window `resolvedAccessToken` (MemberProfilePage.tsx)
  // withholds `accessToken` for (see its doc comment) while a real token mints
  // in the background — a HUNG (never-settling) mint would otherwise leave
  // `hasLink` true forever with no recovery. Past `SILENT_MINT_TIMEOUT_MS`,
  // report `hasLink: false` so that gate falls back to the sentinel →
  // PpLoginModal click-to-recover path instead of hiding Create/Duplicate/
  // Modify indefinitely.
  if (!userEligible) return { gateOpen: true, hasLink: !silentMintHung };

  if (patientResult?.key === patientKey) {
    // Ground truth for THIS patient has resolved — authoritative, in either
    // direction (BLOCKER fix: the claims-only guess never gets the final say).
    if (patientResult.reason === "preflight-error") {
      return { gateOpen: false, hasLink: true, reason: "preflight-error" };
    }
    if (!patientResult.eligible) {
      return { gateOpen: false, hasLink: true, reason: patientResult.reason ?? "preflight-error" };
    }
    return { gateOpen: true, hasLink: true };
  }

  // Real check still in flight for this patient — the claims-only guess is
  // a HINT, never a hard block: stay open (transient "still checking").
  return { gateOpen: true, hasLink: true };
}
